import { forwardRef } from 'react';
import styled from 'styled-components';
import { flexbox, layout, space } from 'styled-system';

import { useTheme } from '@hivebrite/design-system/Theme';

import { GridHelper } from './GridHelper';
import GridContext from '../contexts/GridContext';
import { useGrid } from '../hooks/useGrid';
import { type GridProps, type GridStyledProps } from '../types';

/**
  - Usage in BO: OK
  - Usage in FO: OK
  - Usage in PO: OK
 */

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      children,
      colGap: overrideColGap,
      columnsInMobile,
      displayGrid,
      mt: overrideMt,
      mx: overrideMx,
      numCols: overrideNumCols,
      rowGap: overrideRowGap,
      ...otherProps
    },
    inputRef,
  ) => {
    const { displayGridHelper, gridContextValue, ref } = useGrid({
      columnsInMobile,
      displayGrid,
      inputRef,
      overrideColGap,
      overrideNumCols,
      overrideRowGap,
    });

    const theme = useTheme();

    return (
      <GridContext.Provider value={gridContextValue}>
        <GridStyled
          mt={overrideMt ?? -gridContextValue.rowGap}
          mx={overrideMx ?? theme.grid.gridGap}
          ref={ref}
          {...otherProps}
        >
          {displayGridHelper && <GridHelper />}
          {typeof children === 'function'
            ? children(gridContextValue)
            : children}
        </GridStyled>
      </GridContext.Provider>
    );
  },
);

const GridStyled = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'alignSelf',
      'height',
      'maxWidth',
      'minHeight',
      'minWidth',
      'mt',
      'mx',
      'my',
      'width',
    ].includes(prop),
})<GridStyledProps>`
  ${space}
  ${layout}
  ${flexbox}
  position: relative;
`;
